/**
 * Puida's wavy section separator and page palette.
 *
 * A port of `puida.com/site/src/lib/separator.ts`, which is itself a port of
 * the original Vue `SectionSeparator`. Sulje uses it so that every Puida page
 * shares one visual signature (design.md §11a); change it there first, then
 * here, and keep `test/separator.test.ts` agreeing with the original.
 *
 * The shape is a chain of cubic Béziers through 4–9 points. Each point gets a
 * sine-distributed delay, and the five layered paths sample the same animation
 * at five time offsets, which is what gives the stacked "ribbon" look.
 * Randomness is injected through `random` so a test, or a server render, can
 * pin the shape while the browser rolls a fresh one on every load.
 */
import { darken, fromHsv, parseHex, toHex, toHsv } from './color';

const DURATION = 1000;
const DELAY_MAX = 400;
/** Time offsets (ms) for path1 … path5 in the original component. */
export const SAMPLE_TIMES = [450, 455, 470, 570, 590] as const;

const cubicOut = (t: number) => {
  const e = t - 1;
  return e * e * e + 1;
};
const cubicInOut = (t: number) => (t < 0.5 ? 4 * t * t * t : 0.5 * Math.pow(2 * t - 2, 3) + 1);

/** Returns [path1, path2, path3, path4, path5] as SVG path data in a 100×100 viewBox. */
export function generateSeparator(bottom: boolean, random: () => number = Math.random): string[] {
  const numPoints = Math.floor(6 * random()) + 4;
  const phase = random() * Math.PI * 2;
  const delays = Array.from(
    { length: numPoints },
    (_, i) => ((Math.sin((i / (numPoints - 1)) * Math.PI * 2 + phase) + 1) / 2) * DELAY_MAX,
  );

  const pathAt = (time: number): string => {
    const pts = delays.map((delay, i) => {
      const ease = i === 1 ? cubicOut : cubicInOut;
      return 100 * ease(Math.min(Math.max(time - delay, 0) / DURATION, 1));
    });
    let d = `M 0,${pts[0]}`;
    for (let s = 0; s < numPoints - 1; s++) {
      const x = ((s + 1) / (numPoints - 1)) * 100;
      const cx = x - ((1 / (numPoints - 1)) * 100) / 2;
      d += ` C ${cx} ${pts[s]} ${cx} ${pts[s + 1]} ${x} ${pts[s + 1]}`;
    }
    return d + (bottom ? ' V 100 H 0 Z' : ' V 0 H 0 Z');
  };

  return SAMPLE_TIMES.map(pathAt);
}

/** Fill colours of the layered paths, in drawing order (back to front) for the top variant. */
export function layerFills(
  color: string,
  fromColor: string,
): Array<{ path: number; fill: string }> {
  return [
    { path: 4, fill: '#00000066' },
    { path: 3, fill: color },
    { path: 2, fill: '#3f951433' },
    { path: 1, fill: '#00000022' },
    { path: 0, fill: fromColor },
  ];
}

export interface Palette {
  /** Random-hue colour with the saturation/value of the brand blue #1867de. */
  bg: string;
  /** Same colour rotated 180° in hue. */
  complement: string;
  /** `bg` darkened for the radial gradient. */
  bgDark: string;
}

/** The Puida brand blue. Only its saturation and value are used. */
export const PUIDA_BLUE = '#1867de';

/** Page palette at a given hue. `randomPalette` is this with a random hue. */
export function paletteAt(hue: number): Palette {
  const [, s, v] = toHsv(parseHex(PUIDA_BLUE));
  const bg = toHex(fromHsv(hue, s, v));
  const complement = toHex(fromHsv((hue + 180) % 360, s, v));
  return { bg, complement, bgDark: darken(bg, 4) };
}

/** Page palette: the original picked a random hue once per page and derived everything from it. */
export function randomPalette(random: () => number = Math.random): Palette {
  return paletteAt(360 * random());
}

export function gradientStyle(p: Palette): { backgroundColor: string; backgroundImage: string } {
  return {
    backgroundColor: p.bgDark,
    backgroundImage: `radial-gradient(circle at top, ${p.bg} 0, ${p.bgDark} 90%)`,
  };
}
